// Column definitions for PathCreationTableView.
// Ported from admin-v2/src/components/RecommendedAssignment/PathCreationTableView.columns.jsx
// Logic unchanged — TypeScript only.

import type { ReactNode } from "react";
import { FilterMatchMode, FilterService } from "primereact/api";
import type { TreeNode } from "primereact/treenode";
import { LESSON_TYPE_DISPLAY_LABELS } from "sj-ui-toolkit/src/atlas/utils";

// Score filter: LTE — show rows where topic score <= filter value
const scoreFilterFunction = (value: unknown, filter: unknown): boolean => {
  if (filter === null || filter === undefined || filter === "") return true;

  let numericValue: number;
  if (typeof value === "number") {
    numericValue = value;
  } else if (typeof value === "string") {
    numericValue = parseFloat(value.replace("%", ""));
  } else {
    return false;
  }

  const filterValue = parseFloat(String(filter));
  if (isNaN(numericValue) || isNaN(filterValue)) return false;

  return numericValue <= filterValue;
};

FilterService.register("scoreLTE", scoreFilterFunction);

// ─── Cell body renderers ──────────────────────────────────────────────────────

const typeTemplate = (node: TreeNode): ReactNode => {
  const data = node.data as { level?: string; type?: string };
  if (data.level !== "lesson" || !data.type) return "";
  return <span>{LESSON_TYPE_DISPLAY_LABELS[data.type] ?? data.type}</span>;
};

const scoreTemplate = (node: TreeNode): ReactNode => {
  const data = node.data as { level?: string; score?: string | number };
  return data.level === "topic" ? `${data.score ?? 0}%` : "";
};

const nameTemplate = (node: TreeNode): ReactNode => {
  const data = node.data as { name?: string };
  return <span className="text-wrap break-words">{data.name}</span>;
};

// ─── Column definition type ───────────────────────────────────────────────────

export interface ColumnDef {
  field: string;
  header: string;
  expander?: boolean;
  sortable?: boolean;
  filter?: boolean;
  filterMatchMode?: string;
  filterPlaceholder?: string;
  filterMaxLength?: number;
  filterMenuStyle?: Record<string, string>;
  filterType?: string;
  body?: (node: TreeNode) => ReactNode;
  style?: Record<string, string>;
  visibleInitially?: boolean;
}

// ─── Column factories ─────────────────────────────────────────────────────────

export const getStaticColumns = (): ColumnDef[] => [
  {
    field: "name",
    header: "Name",
    body: nameTemplate,
    expander: true,
    sortable: true,
    filter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterPlaceholder: "Search name...",
  },
];

export const getConfigurableColumns = (): ColumnDef[] => [
  {
    field: "category",
    header: "Category",
    visibleInitially: false,
    sortable: true,
    filter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterPlaceholder: "Search category...",
    filterMaxLength: 30,
    filterMenuStyle: { width: "16rem" },
    style: { width: "10%" },
  },
  {
    field: "topic",
    header: "Topic",
    visibleInitially: true,
    sortable: true,
    filter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterPlaceholder: "Search topic...",
    filterMaxLength: 30,
    filterMenuStyle: { width: "16rem" },
    style: { width: "25%" },
  },
  {
    field: "type",
    header: "Type",
    visibleInitially: true,
    sortable: true,
    filter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterPlaceholder: "Search type...",
    filterMaxLength: 20,
    filterMenuStyle: { width: "16rem" },
    body: typeTemplate,
    style: { width: "8%" },
  },
  {
    field: "duration",
    header: "Duration",
    visibleInitially: false,
    sortable: true,
    filter: true,
    filterMatchMode: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO,
    filterPlaceholder: "Min duration...",
    filterType: "number",
    filterMaxLength: 3,
    filterMenuStyle: { width: "16rem" },
    style: { width: "6%" },
  },
  {
    field: "score",
    header: "Score",
    visibleInitially: true,
    sortable: true,
    filter: true,
    filterMatchMode: "scoreLTE",
    filterPlaceholder: "Max score %",
    filterType: "number",
    filterMaxLength: 3,
    filterMenuStyle: { width: "16rem" },
    body: scoreTemplate,
    style: { width: "6%" },
  },
];

export const getInitialFilters = (
  columns: ColumnDef[]
): Record<string, { value: unknown; matchMode: string }> => {
  const filters: Record<string, { value: unknown; matchMode: string }> = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  };

  columns.forEach(col => {
    if (col.filter) {
      filters[col.field] = {
        value: null,
        matchMode: col.filterMatchMode ?? FilterMatchMode.CONTAINS,
      };
    }
  });

  return filters;
};
